import type { ConvertToCustomerDto, CreateProspectiveCustomerDto, CreateProspectiveCustomerVisitDto, CustomerDto, ProspectiveCustomerDto, ProspectiveCustomerListDto, ProspectiveCustomerVisitDto, ProspectiveCustomerVisitsByConvertedCustomerDto, UpdateProspectiveCustomerDto, UpdateProspectiveCustomerVisitDto } from './models';
import { RestService, Rest } from '@abp/ng.core';
import type { PagedResultDto } from '@abp/ng.core';
import { Injectable } from '@angular/core';
import type { DataRequestDto, EntitySchemaDto, IdNameDto, PageRequestDto } from '../models';

@Injectable({
  providedIn: 'root',
})
export class ProspectiveCustomerService {
  apiName = 'Default';
  

  convertToCustomer = (id: string, input: ConvertToCustomerDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, CustomerDto>({
      method: 'POST',
      url: `/api/app/prospective-customer/${id}/convert-to-customer`,
      body: input,
    },
    { apiName: this.apiName,...config });
  

  create = (input: CreateProspectiveCustomerDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, ProspectiveCustomerDto>({
      method: 'POST',
      url: '/api/app/prospective-customer',
      body: input,
    },
    { apiName: this.apiName,...config });
  

  createVisit = (input: CreateProspectiveCustomerVisitDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, ProspectiveCustomerVisitDto>({
      method: 'POST',
      url: '/api/app/prospective-customer/visit',
      body: input,
    },
    { apiName: this.apiName,...config });
  

  delete = (id: string, config?: Partial<Rest.Config>) =>
    this.restService.request<any, void>({
      method: 'DELETE',
      url: `/api/app/prospective-customer/${id}`,
    },
    { apiName: this.apiName,...config });
  

  deleteVisit = (id: string, config?: Partial<Rest.Config>) =>
    this.restService.request<any, void>({
      method: 'DELETE',
      url: `/api/app/prospective-customer/${id}/visit`,
    },
    { apiName: this.apiName,...config });
  

  get = (id: string, config?: Partial<Rest.Config>) =>
    this.restService.request<any, ProspectiveCustomerDto>({
      method: 'GET',
      url: `/api/app/prospective-customer/${id}`,
    },
    { apiName: this.apiName,...config });
  

  getCount = (input: PageRequestDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, number>({
      method: 'GET',
      url: '/api/app/prospective-customer/count',
      params: { filter: input.filter, onlyActive: input.onlyActive, additionalData: input.additionalData, ignoreMaxResultCount: input.ignoreMaxResultCount, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
    },
    { apiName: this.apiName,...config });
  

  getDropDownOptions = (input: DataRequestDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, PagedResultDto<IdNameDto>>({
      method: 'GET',
      url: '/api/app/prospective-customer/drop-down-options',
      params: { select: input.select, filter: input.filter, onlyActive: input.onlyActive, additionalData: input.additionalData, ignoreMaxResultCount: input.ignoreMaxResultCount, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
    },
    { apiName: this.apiName,...config });
  

  getExpandById = (id: string, config?: Partial<Rest.Config>) =>
    this.restService.request<any, ProspectiveCustomerDto[]>({
      method: 'GET',
      url: `/api/app/prospective-customer/${id}/expand`,
    },
    { apiName: this.apiName,...config });
  

  getExtraPropertiesById = (id: string, config?: Partial<Rest.Config>) =>
    this.restService.request<any, Record<string, object>>({
      method: 'GET',
      url: `/api/app/prospective-customer/${id}/extra-properties`,
    },
    { apiName: this.apiName,...config });
  

  getFullListWithOutDetails = (input: PageRequestDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, ProspectiveCustomerListDto[]>({
      method: 'GET',
      url: '/api/app/prospective-customer/full-list-with-out-details',
      params: { filter: input.filter, onlyActive: input.onlyActive, additionalData: input.additionalData, ignoreMaxResultCount: input.ignoreMaxResultCount, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
    },
    { apiName: this.apiName,...config });
  

  getIds = (input: DataRequestDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, string[]>({
      method: 'GET',
      url: '/api/app/prospective-customer/ids',
      params: { select: input.select, filter: input.filter, onlyActive: input.onlyActive, additionalData: input.additionalData, ignoreMaxResultCount: input.ignoreMaxResultCount, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
    },
    { apiName: this.apiName,...config });
  

  getList = (input: PageRequestDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, PagedResultDto<ProspectiveCustomerDto>>({
      method: 'GET',
      url: '/api/app/prospective-customer',
      params: { filter: input.filter, onlyActive: input.onlyActive, additionalData: input.additionalData, ignoreMaxResultCount: input.ignoreMaxResultCount, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
    },
    { apiName: this.apiName,...config });
  

  getListWithOutDetails = (input: PageRequestDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, PagedResultDto<ProspectiveCustomerListDto>>({
      method: 'GET',
      url: '/api/app/prospective-customer/with-out-details',
      params: { filter: input.filter, onlyActive: input.onlyActive, additionalData: input.additionalData, ignoreMaxResultCount: input.ignoreMaxResultCount, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
    },
    { apiName: this.apiName,...config });
  

  getOneExtraPropertiesByIdAndKey = (id: string, key: string, config?: Partial<Rest.Config>) =>
    this.restService.request<any, object>({
      method: 'GET',
      url: `/api/app/prospective-customer/${id}/one-extra-properties`,
      params: { key },
    },
    { apiName: this.apiName,...config });
  

  getSchema = (config?: Partial<Rest.Config>) =>
    this.restService.request<any, EntitySchemaDto>({
      method: 'GET',
      url: '/api/app/prospective-customer/schema',
    },
    { apiName: this.apiName,...config });
  

  getVisitsByConvertedCustomerId = (convertedCustomerId: string, config?: Partial<Rest.Config>) =>
    this.restService.request<any, ProspectiveCustomerVisitsByConvertedCustomerDto>({
      method: 'GET',
      url: `/api/app/prospective-customer/visits-by-converted-customer-id/${convertedCustomerId}`,
    },
    { apiName: this.apiName,...config });
  

  getVisitsByConvertedMainGroupId = (mainGroupId: string, config?: Partial<Rest.Config>) =>
    this.restService.request<any, ProspectiveCustomerVisitsByConvertedCustomerDto>({
      method: 'GET',
      url: `/api/app/prospective-customer/visits-by-converted-main-group-id/${mainGroupId}`,
    },
    { apiName: this.apiName,...config });
  

  getVisitsByprospectiveCustomerId = (prospectiveCustomerId: string, config?: Partial<Rest.Config>) =>
    this.restService.request<any, ProspectiveCustomerVisitDto[]>({
      method: 'GET',
      url: `/api/app/prospective-customer/visits-byprospective-customer-id/${prospectiveCustomerId}`,
    },
    { apiName: this.apiName,...config });
  

  getWithDetails = (id: string, config?: Partial<Rest.Config>) =>
    this.restService.request<any, ProspectiveCustomerDto>({
      method: 'GET',
      url: `/api/app/prospective-customer/${id}/with-details`,
    },
    { apiName: this.apiName,...config });
  

  setExtraPropertiesByIdAndKeyAndValue = (id: string, key: string, value: string, config?: Partial<Rest.Config>) =>
    this.restService.request<any, void>({
      method: 'POST',
      url: `/api/app/prospective-customer/${id}/set-extra-properties`,
      params: { key, value },
    },
    { apiName: this.apiName,...config });
  

  update = (id: string, input: UpdateProspectiveCustomerDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, ProspectiveCustomerDto>({
      method: 'PUT',
      url: `/api/app/prospective-customer/${id}`,
      body: input,
    },
    { apiName: this.apiName,...config });
  

  updateVisit = (id: string, input: UpdateProspectiveCustomerVisitDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, ProspectiveCustomerVisitDto>({
      method: 'PUT',
      url: `/api/app/prospective-customer/${id}/visit`,
      body: input,
    },
    { apiName: this.apiName,...config });

  constructor(private restService: RestService) {}
}
